import clsx from 'clsx';
import React, { useState } from 'react';

import Button from '@components/Button';
import IconArrowDown from '@components/Images/Rebranding/IconArrowDown';
import OAuthButtons, { OAuthButtonsProps } from '@components/OAuthButtons';
import useEventTracking from '@hooks/useEventTracking';
import { Theme } from '@interfaces/global';
import { ClickTarget, click } from '@tracking';

interface ExpandableOAuthButtonsProps extends Omit<OAuthButtonsProps, 'buttonWidth'> {
  theme?: Theme;
}

const ExpandableOAuthButtons = ({
  articleId,
  briefingGuid,
  className,
  isGdprOptIn,
  leadType,
  newsletterIds,
  oauthButtonsClassName,
  redirectToOnSuccess,
  shareGuid,
  theme = Theme.Light,
  unlockContent,
}: ExpandableOAuthButtonsProps) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { trackEvent } = useEventTracking();

  const handleTrackEvent = () => {
    trackEvent(
      click({
        clickTarget: isMenuOpen ? ClickTarget.CollapseOAuthButtons : ClickTarget.ExpandOAuthButtons,
      })
    );
  };

  return (
    <>
      <Button
        as="button"
        className={clsx(
          'h-40 w-full touch-manipulation border-0 bg-transparent p-0 text-center font-suisse-screen text-disclosure-sm sm:-mb-16 sm:block md:-mb-8 md:text-left',
          {
            'text-white': theme === Theme.Dark,
            'text-grayscale-gray': theme === Theme.Light,
          },
          className
        )}
        onClick={() => setIsMenuOpen((prevState) => !prevState)}
        onClickCapture={handleTrackEvent}
      >
        Continue with Google or Apple
        <IconArrowDown
          className={clsx('ml-8 h-10 w-20 fill-primary-magenta', {
            'rotate-180 transform': isMenuOpen,
            'rotate-0 transform': !isMenuOpen,
          })}
        />
      </Button>
      <div
        // TODO TI-113 Remove these direct descendant selectors once we choose a winning experiment
        // {@link https://app.asana.com/0/1208196937186367/1208342331639358/f}
        className={clsx(
          'text-grayscale-black mb-8 overflow-hidden transition-all duration-500 ease-in-out *:*:w-[209px] sm:*:last-of-type:mt-8 md:flex md:justify-between md:*:last-of-type:mt-0',
          {
            'max-h-[90px] sm:max-h-[100px] md:max-h-56': isMenuOpen,
            'invisible max-h-0': !isMenuOpen,
          }
        )}
      >
        <OAuthButtons
          articleId={articleId}
          briefingGuid={briefingGuid}
          buttonWidth={209}
          isGdprOptIn={isGdprOptIn}
          leadType={leadType}
          newsletterIds={newsletterIds}
          oauthButtonsClassName={oauthButtonsClassName}
          redirectToOnSuccess={redirectToOnSuccess}
          shareGuid={shareGuid}
          unlockContent={unlockContent}
        />
      </div>
    </>
  );
};

export default ExpandableOAuthButtons;
